import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import getPublicCommentsQuery from '../../graphql/query/getPublicComments.query';
import getPublicCommentsGuestQuery from '../../graphql/query/getPublicCommentsGuest.query';
import createPublicCommentMutation from '../../graphql/mutations/createPublicComment.mutation';
import { useCurrentUser, useCurrentUserProfile } from '../../utils';
import { useGuestAuth } from '../../context/GuestAuthContext';
import Avatar from '@mui/material/Avatar';
import SendIcon from '@mui/icons-material/Send';
import ReplyIcon from '@mui/icons-material/Reply';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import VerifyEmailDialog from '../VerifyEmailDialog';
import './PublicComments.css';

interface User {
  id: number;
  firstname?: string;
  lastname?: string;
  email?: string;
  profilePictureUrl?: string;
}

interface Comment {
  id: number;
  listingId: number;
  userId: number;
  parentCommentId: number | null;
  content: string;
  createdAt: string;
  updatedAt: string;
  userByUserId: User;
}

interface PublicCommentsProps {
  listingId: number;
  sellerId: number;
  darkMode?: boolean;
}

const PublicComments: React.FC<PublicCommentsProps> = ({ listingId, sellerId, darkMode = false }) => {
  const currentUser = useCurrentUser();
  const { openSignIn } = useGuestAuth();
  const { emailVerified, profile, refetch: refetchProfile } = useCurrentUserProfile();
  const [newQuestion, setNewQuestion] = useState('');
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [verifyOpen, setVerifyOpen] = useState(false);
  const isUnverified = emailVerified === false;
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;

  const { data, loading, error, refetch } = useQuery(
    currentUser ? getPublicCommentsQuery : getPublicCommentsGuestQuery,
    {
      variables: { listingId },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [createComment, { loading: submitting }] = useMutation(createPublicCommentMutation, {
    onCompleted: () => {
      setNewQuestion('');
      setReplyContent('');
      setReplyingTo(null);
      refetch();
    },
    onError: (error) => {
      console.error('Error creating comment:', error);
      alert('Failed to post comment. Please try again.');
    },
  });

  const comments: Comment[] = data?.allPublicComments?.nodes || [];

  // Organize comments into questions (parent) and answers (replies)
  const questions = comments.filter((c) => c.parentCommentId === null);
  const answersByQuestionId = comments.reduce((acc, comment) => {
    if (comment.parentCommentId !== null) {
      if (!acc[comment.parentCommentId]) {
        acc[comment.parentCommentId] = [];
      }
      acc[comment.parentCommentId].push(comment);
    }
    return acc;
  }, {} as Record<number, Comment[]>);

  const handleSubmitQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !newQuestion.trim()) return;

    await createComment({
      variables: {
        listingId,
        userId: currentUser.id,
        content: newQuestion.trim(),
        parentCommentId: null,
      },
    });
  };

  const handleSubmitReply = async (questionId: number) => {
    if (!currentUser || !replyContent.trim()) return;

    await createComment({
      variables: {
        listingId,
        userId: currentUser.id,
        content: replyContent.trim(),
        parentCommentId: questionId,
      },
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getUserDisplayName = (user: User, isAuthorSeller: boolean) => {
    if (!currentUser) {
      return isAuthorSeller ? 'Seller' : 'Member';
    }
    if (user.firstname && user.lastname) {
      return `${user.firstname} ${user.lastname}`;
    }
    if (user.firstname) {
      return user.firstname;
    }
    return user.email?.split('@')[0] || 'User';
  };

  const getInitials = (user: User, isAuthorSeller: boolean) => {
    if (!currentUser) {
      return isAuthorSeller ? 'S' : 'M';
    }
    if (user.firstname && user.lastname) {
      return `${user.firstname[0]}${user.lastname[0]}`.toUpperCase();
    }
    if (user.firstname) {
      return user.firstname[0].toUpperCase();
    }
    return user.email?.[0]?.toUpperCase() || 'U';
  };

  const isSeller = currentUser?.id === sellerId;
  const canAskQuestion = currentUser && !isSeller;
  const canReply = currentUser && isSeller;

  if (loading && !data) {
    return (
      <div className={`public-comments ${darkMode ? 'dark' : ''}`}>
        <div className="comments-loading">Loading Q&A...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`public-comments ${darkMode ? 'dark' : ''}`}>
        <div className="comments-error">Failed to load Q&A. Please try again.</div>
      </div>
    );
  }

  return (
    <div className={`public-comments ${darkMode ? 'dark' : ''}`}>
      <div className="comments-header">
        <QuestionAnswerIcon className="comments-header-icon" />
        <h3>Questions & Answers</h3>
        <span className="comments-count">({questions.length})</span>
      </div>

      {/* Question Form for Buyers */}
      {canAskQuestion && (
        <form className="question-form" onSubmit={handleSubmitQuestion}>
          {isUnverified && (
            <p className="comments-verify-hint">
              Verify your email to ask a question.{' '}
              <button
                type="button"
                className="comments-verify-link"
                onClick={() => setVerifyOpen(true)}
              >
                Verify now
              </button>
            </p>
          )}
          <div className="question-input-wrapper">
            <Avatar
              src={undefined}
              sx={{ width: 36, height: 36, fontSize: 14 }}
            >
              {currentUser?.id ? 'U' : 'U'}
            </Avatar>
            <input
              type="text"
              placeholder={isUnverified ? 'Verify your email to ask a question' : 'Ask a question about this vehicle...'}
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              disabled={submitting || isUnverified}
              className="question-input"
            />
            <button
              type="submit"
              disabled={!newQuestion.trim() || submitting || isUnverified}
              className="submit-question-btn"
            >
              <SendIcon fontSize="small" />
            </button>
          </div>
        </form>
      )}

      {!currentUser && (
        <div className="login-prompt">
          <button type="button" className="login-prompt-link" onClick={openSignIn}>
            Log in
          </button>{' '}
          to ask a question
        </div>
      )}

      {isSeller && (
        <div className="seller-notice">
          You are the seller. You can respond to buyer questions below.
        </div>
      )}

      {/* Questions List */}
      <div className="questions-list">
        {questions.length === 0 ? (
          <div className="no-questions">
            No questions yet. Be the first to ask!
          </div>
        ) : (
          questions.map((question) => {
            const answers = answersByQuestionId[question.id] || [];
            const isSellerQuestion = question.userId === sellerId;

            return (
              <div key={question.id} className="question-thread">
                {/* Question */}
                <div className="question-item">
                  <div className="comment-avatar">
                    <Avatar
                      src={currentUser ? question.userByUserId.profilePictureUrl || undefined : undefined}
                      sx={{ width: 40, height: 40, fontSize: 14 }}
                    >
                      {getInitials(question.userByUserId, isSellerQuestion)}
                    </Avatar>
                  </div>
                  <div className="comment-content">
                    <div className="comment-header">
                      <span className="comment-author">
                        {getUserDisplayName(question.userByUserId, isSellerQuestion)}
                      </span>
                      {isSellerQuestion && currentUser && (
                        <span className="seller-badge">Seller</span>
                      )}
                      <span className="comment-time">
                        {formatDate(question.createdAt)}
                      </span>
                    </div>
                    <p className="comment-text">{question.content}</p>
                    
                    {/* Reply button for seller */}
                    {canReply && replyingTo !== question.id && (
                      <button
                        className="reply-btn"
                        onClick={() => {
                          setReplyingTo(question.id);
                          setReplyContent('');
                        }}
                      >
                        <ReplyIcon fontSize="small" />
                        Reply
                      </button>
                    )}
                  </div>
                </div>

                {/* Reply Form (for seller) */}
                {replyingTo === question.id && canReply && (
                  <div className="reply-form">
                    {isUnverified && (
                      <p className="comments-verify-hint">
                        Verify your email to reply.{' '}
                        <button
                          type="button"
                          className="comments-verify-link"
                          onClick={() => setVerifyOpen(true)}
                        >
                          Verify now
                        </button>
                      </p>
                    )}
                    <div className="reply-input-wrapper">
                      <textarea
                        placeholder={isUnverified ? 'Verify your email to reply' : 'Write your answer...'}
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        disabled={submitting || isUnverified}
                        className="reply-input"
                        rows={2}
                      />
                      <div className="reply-actions">
                        <button
                          type="button"
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyContent('');
                          }}
                          className="cancel-reply-btn"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSubmitReply(question.id)}
                          disabled={!replyContent.trim() || submitting || isUnverified}
                          className="submit-reply-btn"
                        >
                          <SendIcon fontSize="small" />
                          Reply
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Answers */}
                {answers.length > 0 && (
                  <div className="answers-list">
                    {answers.map((answer) => {
                      const isSellerAnswer = answer.userId === sellerId;
                      return (
                        <div key={answer.id} className="answer-item">
                          <div className="comment-avatar">
                            <Avatar
                              src={currentUser ? answer.userByUserId.profilePictureUrl || undefined : undefined}
                              sx={{ width: 32, height: 32, fontSize: 12 }}
                            >
                              {getInitials(answer.userByUserId, isSellerAnswer)}
                            </Avatar>
                          </div>
                          <div className="comment-content">
                            <div className="comment-header">
                              <span className="comment-author">
                                {getUserDisplayName(answer.userByUserId, isSellerAnswer)}
                              </span>
                              {isSellerAnswer && currentUser && (
                                <span className="seller-badge">Seller</span>
                              )}
                              <span className="comment-time">
                                {formatDate(answer.createdAt)}
                              </span>
                            </div>
                            <p className="comment-text">{answer.content}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      {token && (
        <VerifyEmailDialog
          open={verifyOpen}
          token={token}
          email={profile?.email}
          darkMode={darkMode}
          onVerified={() => {
            setVerifyOpen(false);
            refetchProfile();
          }}
          onClose={() => setVerifyOpen(false)}
        />
      )}
    </div>
  );
};

export default PublicComments;
